import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Layout, notification, Select, Input, Pagination, Button, Tooltip, Typography, Modal, Switch } from 'antd';
import { FileExcelOutlined, CloseCircleOutlined, CheckCircleOutlined, MessageOutlined, PaperClipOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import AddForm from './IncidentAdd';
import IncidentsTable from './IncidentsTable';
import IncidentModal from './IncidentModal';
import { SkelList, SkelRecent, SkelStats } from './Skeleton';
import LoginRequired from './LoginRequired';
import { STATUS_META, STATUS_OPTIONS, STATUS_ORDER, OPEN_STATUSES, RESOLVED_STATUSES, LIST_STATUS_META } from './statusMeta';
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';

const { Content } = Layout;

const MIN_LOAD_MS = 1500;

const afterMinDelay = (startedAt, fetchId, currentIdRef, done) => {
  const wait = Math.max(0, MIN_LOAD_MS - (Date.now() - startedAt));
  setTimeout(() => {
    if (fetchId === currentIdRef.current) done();
  }, wait);
};

const STATS_PERIOD_OPTS = [
  { value: 'all', label: 'Všetko' },
  { value: '30', label: 'Posledných 30 dní' },
  { value: '70', label: 'Posledných 70 dní' },
  { value: '150', label: 'Posledných 150 dní' },
  { value: '365', label: 'Posledných 365 dní' },
  { value: '720', label: 'Posledných 720 dní' },
];

const MONTH_OPTS = [
  { value: 1, label: 'január' },
  { value: 2, label: 'február' },
  { value: 3, label: 'marec' },
  { value: 4, label: 'apríl' },
  { value: 5, label: 'máj' },
  { value: 6, label: 'jún' },
  { value: 7, label: 'júl' },
  { value: 8, label: 'august' },
  { value: 9, label: 'september' },
  { value: 10, label: 'október' },
  { value: 11, label: 'november' },
  { value: 12, label: 'december' },
];

/* SVG donut – only open statuses (1 + 2 + 6); closed counts shown separately below */
const OpenStatusDonut = ({ received, inProgress, onTest, resolved, rejected, backlog, onSliceClick }) => {
  const openTotal = received + inProgress + onTest;
  const size = 118;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;

  const slices = [
    { key: '1', value: received, color: '#5abbff', label: 'Prijaté' },
    { key: '2', value: inProgress, color: '#e88e39', label: 'V riešení' },
    { key: '6', value: onTest, color: STATUS_META[6].color, label: 'Na teste' },
  ].filter(s => s.value > 0);

  let offset = 0;
  const arcs = openTotal > 0
    ? slices.map(s => {
        const len = (s.value / openTotal) * c;
        const arc = { ...s, dash: len, offset };
        offset += len;
        return arc;
      })
    : [];

  return (
    <div className="inc-donut-wrap">
      <div className="inc-donut-row">
        <div className="inc-donut-chart">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle
              cx={size / 2} cy={size / 2} r={r}
              fill="none" stroke="#2a2b2e" strokeWidth={stroke}
            />
            {arcs.length === 0 ? null : arcs.map(a => (
              <circle
                key={a.key}
                cx={size / 2} cy={size / 2} r={r}
                fill="none"
                stroke={a.color}
                strokeWidth={stroke}
                strokeDasharray={`${a.dash} ${c - a.dash}`}
                strokeDashoffset={-a.offset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ cursor: onSliceClick ? 'pointer' : 'default', transition: 'stroke-width 0.15s' }}
                onClick={() => onSliceClick && onSliceClick(a.key)}
              />
            ))}
          </svg>
          <div className="inc-donut-center">
            <div className="inc-donut-center-val">{openTotal}</div>
            <div className="inc-donut-center-lbl">otvorené</div>
          </div>
        </div>

        <div className="inc-donut-tiles">
          <button type="button" className="inc-donut-closed-item" onClick={() => onSliceClick && onSliceClick('1')}>
            <span>Prijaté</span>
            <strong style={{ color: '#5abbff' }}>{received}</strong>
          </button>
          <button type="button" className="inc-donut-closed-item" onClick={() => onSliceClick && onSliceClick('2')}>
            <span>V riešení</span>
            <strong style={{ color: '#e88e39' }}>{inProgress}</strong>
          </button>
          <button type="button" className="inc-donut-closed-item" onClick={() => onSliceClick && onSliceClick('6')}>
            <span>Na teste</span>
            <strong style={{ color: STATUS_META[6].color }}>{onTest}</strong>
          </button>
          <div className="inc-donut-closed-item">
            <span>Vyriešené</span>
            <strong style={{ color: '#52c41a' }}>{resolved}</strong>
          </div>
          <div className="inc-donut-closed-item">
            <span>Zamietnuté</span>
            <strong style={{ color: '#ff5a5a' }}>{rejected}</strong>
          </div>
          <button type="button" className="inc-donut-closed-item" onClick={() => onSliceClick && onSliceClick('5')}>
            <span>Backlog</span>
            <strong style={{ color: '#a78bfa' }}>{backlog}</strong>
          </button>
        </div>
      </div>
    </div>
  );
};

const systemName = (row) => {
  const cat = (row.category || '').trim();
  return cat || '(bez systému)';
};

const sortByTimeDesc = (a, b) => {
  const ta = new Date(a.created || a.datetime || 0).getTime();
  const tb = new Date(b.created || b.datetime || 0).getTime();
  return tb - ta;
};

/** Group rows by system/category only */
const groupRowsBySystem = (rows) => {
  const list = rows || [];
  if (!list.length) return [];

  const bySystem = {};
  list.forEach(r => {
    const key = systemName(r);
    if (!bySystem[key]) bySystem[key] = [];
    bySystem[key].push(r);
  });

  return Object.keys(bySystem)
    .sort((a, b) => a.localeCompare(b, 'sk'))
    .map(name => ({
      name,
      rows: bySystem[name].sort(sortByTimeDesc),
    }));
};

const ActivityDayModal = ({ open, loading, data, onClose, onOpenIncident }) => {
  const s = data?.summary || {};

  const renderGroupedRows = (rows) => {
    const systems = groupRowsBySystem(rows);
    if (!systems.length) {
      return <div className="inc-act-empty">Žiadne záznamy</div>;
    }
    return systems.map(sys => (
      <div key={sys.name} className="inc-act-sysgroup">
        <div className="inc-act-sysgroup-label">
          {sys.name}
          <span className="inc-act-section-count">{sys.rows.length}</span>
        </div>
        {sys.rows.map((row, idx) => {
          const time = row.created || row.datetime;
          return (
            <div
              key={row.commentId || `${row.id}-${idx}`}
              className="inc-act-row"
              onClick={() => onOpenIncident(row.id)}
              title={row.text}
            >
              <span className="inc-act-niceid">{row.niceid}</span>
              <span className="inc-act-row-text">{row.text}</span>
              <span className="inc-act-time">{time ? moment(time).format('HH:mm') : ''}</span>
            </div>
          );
        })}
      </div>
    ));
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      title={
        <span style={{ color: '#e0e0dc' }}>
          Aktivita · {data?.label || '—'}
        </span>
      }
      styles={{
        content: { background: '#1e1f22', border: '1px solid #2e2f33' },
        header: { background: '#1e1f22', borderBottom: '1px solid #2e2f33' },
        body: { maxHeight: '70vh', overflowY: 'auto', padding: '14px 16px' },
      }}
    >
      {loading || !data ? (
        <SkelList rows={10} />
      ) : (
        <div className="fade-in">
          <div className="inc-act-summary">
            <div className="inc-act-sum-item">
              <span>Požadované</span>
              <strong style={{ color: '#5abbff' }}>{s.requested || 0}</strong>
            </div>
            <div className="inc-act-sum-item">
              <span>Vyriešené</span>
              <strong style={{ color: '#52c41a' }}>{s.resolved || 0}</strong>
            </div>
          </div>

          {[
            { key: 'requested', title: 'Požadované', color: '#5abbff', rows: data.requested },
            { key: 'resolved', title: 'Vyriešené', color: '#52c41a', rows: data.resolved },
          ].map(section => (
            <div key={section.key} className="inc-act-section">
              <div className="inc-act-section-title" style={{ color: section.color }}>
                {section.title}
                <span className="inc-act-section-count">{(section.rows || []).length}</span>
              </div>
              {renderGroupedRows(section.rows)}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

const StatusChangesBars = ({ series, periodLabel, onDayClick }) => {
  const activeSeries = (series || []).filter(d =>
    (Number(d.requested) || 0) + (Number(d.resolved) || 0) > 0
  );

  const isWeekendDay = (d) => {
    if (d.weekend) return true;
    // day buckets only – week/month aggregates aren't weekends
    if (d.bucket && d.bucket !== 'day') return false;
    const dow = moment(d.day, 'YYYY-MM-DD').day();
    return dow === 0 || dow === 6;
  };

  const max = Math.max(
    1,
    ...activeSeries.flatMap(d => [Number(d.requested) || 0, Number(d.resolved) || 0])
  );
  const sumRequested = activeSeries.reduce((s, d) => s + (Number(d.requested) || 0), 0);
  const sumResolved = activeSeries.reduce((s, d) => s + (Number(d.resolved) || 0), 0);

  const barH = (v) => Math.max(v ? 10 : 0, Math.round(((Number(v) || 0) / max) * 100));

  const tooltipContent = (d, weekend) => (
    <div className="inc-bars-tip">
      <div className="inc-bars-tip-day">{d.label}{weekend ? ' · víkend' : ''}</div>
      <div className="inc-bars-tip-row">
        <span className="inc-bars-tip-dot" style={{ background: '#5abbff' }} />
        <span>Požiadané</span>
        <strong>{d.requested || 0}</strong>
      </div>
      <div className="inc-bars-tip-row">
        <span className="inc-bars-tip-dot" style={{ background: '#52c41a' }} />
        <span>Vyriešené</span>
        <strong>{d.resolved || 0}</strong>
      </div>
      <div className="inc-bars-tip-hint">Kliknite pre detail</div>
    </div>
  );

  if (!activeSeries.length) {
    return (
      <div className="inc-bars-card">
        <div className="inc-bars-heading">
          <span>Aktivita · {periodLabel}</span>
          <span className="inc-bars-total">0/0</span>
        </div>
        <div className="inc-bars-empty">Žiadna aktivita v období</div>
      </div>
    );
  }

  return (
    <div className="inc-bars-card">
      <div className="inc-bars-heading">
        <span>Aktivita · {periodLabel}</span>
        <span className="inc-bars-total">{sumRequested}/{sumResolved}</span>
      </div>
      <div className="inc-bars-chart">
        {activeSeries.map(d => {
          const weekend = isWeekendDay(d);
          return (
            <Tooltip
              key={d.day}
              title={tooltipContent(d, weekend)}
              placement="top"
              mouseEnterDelay={0.05}
              color="#1e1f22"
            >
              <div
                className={`inc-bars-col${weekend ? ' weekend' : ' weekday'}`}
                style={{ cursor: 'pointer' }}
                onClick={() => onDayClick && onDayClick(d)}
              >
                <div className="inc-bars-track">
                  <div className="inc-bars-group">
                    <div className="inc-bars-fill requested" style={{ height: `${barH(d.requested)}%` }} />
                    <div className="inc-bars-fill resolved" style={{ height: `${barH(d.resolved)}%` }} />
                  </div>
                </div>
                <div className="inc-bars-label">{d.label}</div>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

const Incidents = ({ loggedInUser }) => {
  const [incidents, setIncidents] = useState([]);
  const [backlogIncidents, setBacklogIncidents] = useState([]);
  const [testIncidents, setTestIncidents] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const listFetchId = useRef(0);
  const [recentDone, setRecentDone] = useState([]);
  const [recentDoneLoading, setRecentDoneLoading] = useState(true);
  const recentDoneFetchId = useRef(0);
  const [recentComments, setRecentComments] = useState([]);
  const [recentCommentsLoading, setRecentCommentsLoading] = useState(true);
  const recentCommentsFetchId = useRef(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [emailOptions, setEmailOptions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [incidentYears, setIncidentYears] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [openStats, setOpenStats] = useState([]);
  const [statusDaily, setStatusDaily] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const statsFetchId = useRef(0);
  const [statsPeriod, setStatsPeriod] = useState('all');
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [dayModalLoading, setDayModalLoading] = useState(false);
  const [dayModalData, setDayModalData] = useState(null);
  const [filters, setFilters] = useState({
    company: '',
    statuses: [],
    categories: [],
    search: '',
    year: undefined,
    monthFrom: undefined,
    monthTo: undefined,
    page: 1,
    pageSize: 30,
  });
  const [showResolved, setShowResolved] = useState(false);
  const [groupBySystem, setGroupBySystem] = useState(false);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [lastLogins, setLastLogins] = useState([]);
  const [lastLoginsLoading, setLastLoginsLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const isSuperuser = loggedInUser && loggedInUser.role === 'SUPERUSER';

  const categoryOpts = useMemo(() => {
    const active = categoryStats
      .filter(c => Number(c.status1) > 0 || Number(c.status2) > 0 || Number(c.status5) > 0 || Number(c.status6) > 0)
      .sort((a, b) => (a.category || '').localeCompare(b.category || '', 'sk'))
      .map(c => ({ value: c.category, label: String(c.category || '').toUpperCase() }));

    const archived = categoryStats
      .filter(c => Number(c.status1) === 0 && Number(c.status2) === 0 && Number(c.status5) === 0 && Number(c.status6) === 0)
      .sort((a, b) => (a.category || '').localeCompare(b.category || '', 'sk'))
      .map(c => ({ value: c.category, label: String(c.category || '').toUpperCase() }));

    const groups = [];
    if (active.length) {
      groups.push({ label: `AKTÍVNE PROJEKTY (${active.length})`, options: active });
    }
    if (archived.length) {
      groups.push({ label: `ARCHIVOVANÉ PROJEKTY (${archived.length})`, options: archived });
    }
    return groups;
  }, [categoryStats]);

  const statusTotals = useMemo(() => (
    openStats.reduce((acc, c) => ({
      received: acc.received + Number(c.status1 || 0),
      inProgress: acc.inProgress + Number(c.status2 || 0),
      onTest: acc.onTest + Number(c.status6 || 0),
      resolved: acc.resolved + Number(c.status3 || 0) + Number(c.status7 || 0),
      rejected: acc.rejected + Number(c.status4 || 0),
      backlog: acc.backlog + Number(c.status5 || 0),
    }), { received: 0, inProgress: 0, onTest: 0, resolved: 0, rejected: 0, backlog: 0 })
  ), [openStats]);

  const statsPeriodLabel = useMemo(
    () => STATS_PERIOD_OPTS.find(o => o.value === statsPeriod)?.label || 'Všetko',
    [statsPeriod]
  );

  const fetchCategoryStats = (company = filters.company) => {
    axios.get(`${process.env.REACT_APP_API_URL}/category-stats`, {
      params: company ? { company } : {},
    })
      .then(r => setCategoryStats(r.data || []))
      .catch(() => setCategoryStats([]));
  };

  const fetchSidebarStats = (company = filters.company, period = statsPeriod) => {
    setStatsLoading(true);
    const fetchId = ++statsFetchId.current;
    const startedAt = Date.now();
    const statsParams = {
      ...(company ? { company } : {}),
      ...(period && period !== 'all' ? { days: period } : {}),
    };
    const dailyParams = {
      days: period === 'all' ? 'all' : period,
      ...(company ? { company } : {}),
    };
    Promise.all([
      axios.get(`${process.env.REACT_APP_API_URL}/category-stats`, { params: statsParams }),
      axios.get(`${process.env.REACT_APP_API_URL}/status-changes-daily`, { params: dailyParams }),
    ])
      .then(([statsRes, dailyRes]) => {
        if (fetchId !== statsFetchId.current) return;
        setOpenStats(statsRes.data || []);
        setStatusDaily(Array.isArray(dailyRes.data) ? dailyRes.data : []);
      })
      .catch(() => {
        if (fetchId !== statsFetchId.current) return;
        setOpenStats([]);
        setStatusDaily([]);
      })
      .finally(() => {
        afterMinDelay(startedAt, fetchId, statsFetchId, () => setStatsLoading(false));
      });
  };

  const openDayDetail = (d) => {
    setDayModalOpen(true);
    setDayModalLoading(true);
    setDayModalData(null);
    axios.get(`${process.env.REACT_APP_API_URL}/activity-day-detail`, {
      params: {
        day: d.day,
        bucket: d.bucket || 'day',
        ...(filters.company ? { company: filters.company } : {}),
      },
    })
      .then(r => setDayModalData(r.data))
      .catch(() => {
        notification.error({ message: 'Nepodarilo sa načítať detail dňa.' });
        setDayModalOpen(false);
      })
      .finally(() => setDayModalLoading(false));
  };

  const fetchIncidents = () => {
    const explicit = (filters.statuses || []).map(String);
    const wantBacklog = explicit.length === 0 || explicit.includes('5');
    // DEPLOYED TO TEST is handled by the tester – own section above the main list
    const wantTest = explicit.length === 0 || explicit.includes('6');
    let mainStatuses = explicit.filter((s) => s !== '5' && s !== '6');
    if (!explicit.length) {
      const defaults = showResolved ? [...OPEN_STATUSES, ...RESOLVED_STATUSES, 4] : OPEN_STATUSES;
      mainStatuses = defaults.map(String).filter((s) => s !== '6');
    }

    const shared = {
      company: filters.company,
      search: filters.search,
      sortField,
      sortOrder,
    };
    if (filters.categories?.length) {
      shared.categories = filters.categories.join(',');
    }
    if (filters.year) shared.year = filters.year;
    if (filters.monthFrom) shared.monthFrom = filters.monthFrom;
    if (filters.monthTo) shared.monthTo = filters.monthTo;

    setListLoading(true);
    const fetchId = ++listFetchId.current;
    const startedAt = Date.now();

    const mainReq = mainStatuses.length
      ? axios.get(`${process.env.REACT_APP_API_URL}/incidents`, {
          params: {
            ...shared,
            page: filters.page,
            pageSize: filters.pageSize,
            statuses: mainStatuses.join(','),
          },
        })
      : Promise.resolve({ data: { data: [], total: 0 } });

    const backlogReq = wantBacklog
      ? axios.get(`${process.env.REACT_APP_API_URL}/incidents`, {
          params: {
            ...shared,
            page: 1,
            pageSize: 200,
            statuses: '5',
          },
        })
      : Promise.resolve({ data: { data: [] } });

    const testReq = wantTest
      ? axios.get(`${process.env.REACT_APP_API_URL}/incidents`, {
          params: {
            ...shared,
            page: 1,
            pageSize: 200,
            statuses: '6',
          },
        })
      : Promise.resolve({ data: { data: [] } });

    Promise.all([mainReq, backlogReq, testReq])
      .then(([mainRes, backlogRes, testRes]) => {
        if (fetchId !== listFetchId.current) return;
        setIncidents(mainRes.data.data || []);
        setTotal(mainRes.data.total || 0);
        setBacklogIncidents(backlogRes.data.data || []);
        setTestIncidents(testRes.data.data || []);
      })
      .catch(() => {
        if (fetchId !== listFetchId.current) return;
        notification.error({
          message: 'Chyba',
          description: 'Existujúce incidenty si viete zobraziť až po prihlásení.',
        });
      })
      .finally(() => {
        afterMinDelay(startedAt, fetchId, listFetchId, () => setListLoading(false));
      });
  };

  const fetchRecentDone = () => {
    setRecentDoneLoading(true);
    const fetchId = ++recentDoneFetchId.current;
    const startedAt = Date.now();
    axios.get(`${process.env.REACT_APP_API_URL}/incidents`, {
      params: {
        statuses: RESOLVED_STATUSES.join(','),
        company: filters.company || '',
        page: 1,
        pageSize: 15,
        sortField: 'lastUpdate',
        sortOrder: 'desc',
      }
    })
      .then((response) => {
        if (fetchId !== recentDoneFetchId.current) return;
        setRecentDone(response.data.data || []);
      })
      .catch(() => {
        if (fetchId !== recentDoneFetchId.current) return;
        setRecentDone([]);
      })
      .finally(() => {
        afterMinDelay(startedAt, fetchId, recentDoneFetchId, () => setRecentDoneLoading(false));
      });
  };

  const fetchRecentComments = () => {
    setRecentCommentsLoading(true);
    const fetchId = ++recentCommentsFetchId.current;
    const startedAt = Date.now();
    axios.get(`${process.env.REACT_APP_API_URL}/comments/recent`, {
      params: { company: filters.company || '', limit: 15 }
    })
      .then((response) => {
        if (fetchId !== recentCommentsFetchId.current) return;
        setRecentComments(response.data || []);
      })
      .catch(() => {
        if (fetchId !== recentCommentsFetchId.current) return;
        setRecentComments([]);
      })
      .finally(() => {
        afterMinDelay(startedAt, fetchId, recentCommentsFetchId, () => setRecentCommentsLoading(false));
      });
  };

  const formatRelative = (dt) => {
    if (!dt) return '';
    const hours = moment().diff(moment(dt), 'hours');
    if (hours < 24) {
      if (hours < 1) {
        const mins = Math.max(1, moment().diff(moment(dt), 'minutes'));
        return `pred ${mins} min.`;
      }
      return `pred ${hours} hod.`;
    }
    return moment(dt).format('D.M.YY');
  };

  const fetchLastLogins = () => {
    if (!isSuperuser) return;
    setLastLoginsLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/users/last-logins`)
      .then((r) => setLastLogins(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLastLogins([]))
      .finally(() => setLastLoginsLoading(false));
  };

  const userDisplayName = (u) => {
    const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    if (full) return full;
    const raw = String(u.username || u.email || '').trim();
    if (!raw) return '—';
    return raw.includes('@') ? raw.split('@')[0] : raw;
  };

  const stripHtml = (html) =>
    String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

  const activityTickerItems = useMemo(() => {
    if (!isSuperuser || !lastLogins.length) return [];

    return lastLogins.map((u) => {
      const name = userDisplayName(u);
      return {
        key: `login-${u.id}`,
        kind: 'login',
        label: name,
        when: u.lastLogin ? formatRelative(u.lastLogin) : '',
        title: `Prihlásenie · ${name}${u.lastLogin ? ` · ${formatRelative(u.lastLogin)}` : ''}`,
      };
    });
  }, [isSuperuser, lastLogins]);

  useEffect(() => {
    if (!loggedInUser) return;
    fetchIncidents();
  }, [filters, sortField, sortOrder, loggedInUser, showResolved]);

  useEffect(() => {
    if (!loggedInUser) return;
    fetchRecentDone();
    fetchRecentComments();
    fetchCategoryStats(filters.company);
    fetchSidebarStats(filters.company, statsPeriod);
    fetchLastLogins();
  }, [filters.company, loggedInUser, statsPeriod]);

  useEffect(() => {
    if (!loggedInUser || !isSuperuser) return undefined;
    const timer = setInterval(fetchLastLogins, 60000);
    return () => clearInterval(timer);
  }, [loggedInUser, isSuperuser]);

  useEffect(() => {
    if (!loggedInUser) return;

    axios.get(`${process.env.REACT_APP_API_URL}/companies`)
      .then(response => {
        setCompanies([{ alias: '', name: 'VŠETKY SPOLOČNOSTI' }, ...response.data, { alias: 'UNKNOWN', name: 'UNKNOWN' }]);
      })
      .catch(() => {
        notification.error({
          message: 'Chyba',
          description: 'Nastala chyba pri načítaní spoločností!'
        });
      });
    axios.get(`${process.env.REACT_APP_API_URL}/incident-years`)
      .then(response => {
        setIncidentYears(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => setIncidentYears([]));
    axios.get(`${process.env.REACT_APP_API_URL}/emails`)
      .then(response => {
        setEmailOptions(response.data);
      })
      .catch(() => {
        notification.error({
          message: 'Chyba',
          description: 'Nastala chyba pri načítaní emailov!'
        });
      });
  }, [loggedInUser]);

  useEffect(() => {
    if (id && loggedInUser) {
      axios.get(`${process.env.REACT_APP_API_URL}/incident/get/${id}`)
        .then((response) => {
          setSelectedIncident(response.data);
          setModalOpen(true);
        })
        .catch(() => {
          notification.error({
            message: 'Chyba',
            description: 'Nastala chyba pri načítaní incidentu!'
          });
        });
    }
  }, [id, loggedInUser]);
  

  const handleFormSubmit = (formData) => {
    axios.post(`${process.env.REACT_APP_API_URL}/submit`, formData)
      .then(() => {
        fetchIncidents();
        fetchRecentDone();
        fetchRecentComments();
        notification.success({
          message: 'Úspech',
          description: 'Incident bol úspešne pridaný!'
        });
      })
      .catch(() => {
        notification.error({
          message: 'Chyba',
          description: 'Nastala chyba pri pridávaní incidentu.'
        });
      });
  };

  const exportToExcel = () => {
    const exportFilters = {
      company: filters.company || undefined,
      search: filters.search || undefined,
      year: filters.year || undefined,
      monthFrom: filters.monthFrom || undefined,
      monthTo: filters.monthTo || undefined,
    };
    // Export vždy všetky statusy (vrátane vyriešených) — bez UI filtra 1+2
    if (filters.categories?.length) {
      exportFilters.categories = filters.categories.join(',');
    }

    axios.get(`${process.env.REACT_APP_API_URL}/incidents/export`, {
      params: exportFilters,
      responseType: 'blob',
    })
      .then((response) => {
        const blob = new Blob(
          [response.data],
          { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `poziadavky-${moment().format('YYYY-MM-DD')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        notification.error({
          message: 'Chyba',
          description: 'Nastala chyba pri exporte požiadaviek.',
        });
      });
  };

  const handleRowClick = (id) => {
    axios.get(`${process.env.REACT_APP_API_URL}/incident/get/${id}`)
      .then((response) => {
        setSelectedIncident(response.data);
        setModalOpen(true);
        navigate(`/incident/${id}`);
      })
      .catch(() => {
        notification.error({
          message: 'Chyba',
          description: 'Nastala chyba pri načítaní incidentu!'
        });
      });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedIncident(null);
    navigate('/'); // Navigate back to the main incidents page
  };

  const handleAddComment = (comment, attachments) => {
    const formData = new FormData();
    Object.keys(comment).forEach(key => {
      formData.append(key, comment[key]);
    });
    attachments.forEach(file => {
      formData.append('attachments', file.originFileObj);
    });

    axios.post(`${process.env.REACT_APP_API_URL}/comment`, formData)
      .then(() => {
        notification.success({
          message: 'Úspech',
          description: 'Komentár bol úspešne pridaný!'
        });
        fetchIncidents();
        fetchRecentComments();
      })
      .catch(() => {
        notification.error({
          message: 'Chyba',
          description: 'Nastala chyba pri pridávaní komentára.'
        });
      });
  };

  const handleFilterChange = (changedFilters) => {
    setFilters({ ...filters, ...changedFilters, page: 1 });  // Reset page to 1 on filter change
  };

  const handlePaginationChange = (page, pageSize) => {
    setFilters({ ...filters, page, pageSize });
  };

  const resetFilters = () => {
    setFilters({
      company: '',
      statuses: [],
      categories: [],
      search: '',
      year: undefined,
      monthFrom: undefined,
      monthTo: undefined,
      page: 1,
      pageSize: 30,
    });
  };

  const hasActiveFilters = !!(
    filters.company
    || (filters.statuses && filters.statuses.length)
    || (filters.categories && filters.categories.length)
    || (filters.search && String(filters.search).trim())
    || filters.year
    || filters.monthFrom
    || filters.monthTo
  );

  if (!loggedInUser) {
    return (
      <Content className="incidents-page" style={{ padding: '1px', minHeight: 280, paddingTop: '0 !important' }}>
        <Typography.Title level={5} className='white'>Založiť incident</Typography.Title>
        <AddForm onFormSubmit={handleFormSubmit} />

        <div className="inc-found-heading" style={{ marginTop: 8, marginBottom: 12 }}>
          <Typography.Title level={5} className="white" style={{ margin: 0 }}>Nájdené incidenty</Typography.Title>
        </div>

        <LoginRequired
          title="Prihláste sa pre zobrazenie požiadaviek"
          subtitle="Zoznam incidentov, štatistiky a komentáre sú dostupné po prihlásení firemným účtom."
        />
      </Content>
    );
  }

  return (
    <Content className="incidents-page" style={{ padding: '1px', minHeight: 280, paddingTop: '0 !important' }}>
      <Typography.Title level={5} className='white'>Založiť incident</Typography.Title>
      <AddForm onFormSubmit={handleFormSubmit} />

      <div className="filters">
        <div className="inc-found-heading">
          <Typography.Title level={5} className="white" style={{ margin: 0 }}>Nájdené incidenty</Typography.Title>
          <div className="inc-found-switches">
            <label className="inc-show-resolved">
              <Switch
                size="small"
                checked={showResolved}
                onChange={(checked) => {
                  setShowResolved(checked);
                  setFilters(prev => ({ ...prev, page: 1 }));
                }}
              />
              <span>Zobraziť aj vyriešené</span>
            </label>
            <label className="inc-show-resolved">
              <Switch
                size="small"
                checked={groupBySystem}
                onChange={setGroupBySystem}
              />
              <span>Kategorizovať podľa systémov</span>
            </label>
          </div>
        </div>
        <div className="inc-filter-bar">
          <Select
            className="inc-filter-chip"
            placeholder="Spoločnosť"
            onChange={(value) => handleFilterChange({ company: value, categories: [] })}
            value={filters.company || undefined}
            allowClear
            popupMatchSelectWidth={false}
            options={companies
              .filter(c => c.alias !== '')
              .map(c => ({ value: c.alias, label: c.name }))}
          />
          <Select
            className="inc-filter-chip"
            placeholder="Rok"
            allowClear
            value={filters.year}
            onChange={(value) => handleFilterChange({ year: value })}
            popupMatchSelectWidth={false}
            options={incidentYears.map((y) => ({ value: y, label: String(y) }))}
          />
          <Select
            className="inc-filter-chip"
            placeholder="Mesiac od"
            allowClear
            value={filters.monthFrom}
            onChange={(value) => handleFilterChange({ monthFrom: value })}
            popupMatchSelectWidth={false}
            options={MONTH_OPTS}
          />
          <Select
            className="inc-filter-chip"
            placeholder="Mesiac do"
            allowClear
            value={filters.monthTo}
            onChange={(value) => handleFilterChange({ monthTo: value })}
            popupMatchSelectWidth={false}
            options={MONTH_OPTS}
          />
          <Select
            className="inc-filter-chip"
            mode="multiple"
            allowClear
            maxTagCount={1}
            placeholder="Stavy"
            value={filters.statuses}
            onChange={(value) => handleFilterChange({ statuses: value || [] })}
            options={STATUS_OPTIONS}
            popupMatchSelectWidth={false}
            menuItemSelectedIcon={null}
            optionRender={(option) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  readOnly
                  checked={(filters.statuses || []).includes(option.value)}
                  style={{ accentColor: '#00b96b' }}
                />
                <span>{option.label}</span>
              </div>
            )}
          />
          <Select
            className="inc-filter-chip inc-filter-chip-wide"
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            maxTagCount={1}
            placeholder="Systémy"
            value={filters.categories}
            onChange={(value) => handleFilterChange({ categories: value || [] })}
            options={categoryOpts}
            popupMatchSelectWidth={280}
            menuItemSelectedIcon={null}
            optionRender={(option) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  readOnly
                  checked={(filters.categories || []).includes(option.value)}
                  style={{ accentColor: '#00b96b' }}
                />
                <span>{option.label}</span>
              </div>
            )}
          />
          <div className="inc-search-wrap">
            <Input
              className="inc-search-input"
              placeholder="Hľadať…"
              value={filters.search}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              onPressEnter={(e) => handleFilterChange({ search: e.target.value })}
              allowClear
            />
            <Button
              type="primary"
              className="inc-search-btn"
              icon={<SearchOutlined />}
              onClick={() => handleFilterChange({ search: filters.search })}
            />
          </div>
          <div className="inc-filter-actions">
            {hasActiveFilters && (
              <Button
                type="primary"
                className="inc-filter-clear"
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={resetFilters}
              >
                Vymazať
              </Button>
            )}
            <Button
              className="inc-filter-action inc-filter-export"
              size="small"
              icon={<FileExcelOutlined />}
              onClick={exportToExcel}
            >
              Excel
            </Button>
            <Select
              className="inc-filter-chip"
              style={{ width: 150 }}
              placeholder="Zoradiť podľa"
              onChange={(value) => setSortField(value)}
              value={sortField}
              popupMatchSelectWidth={false}
              options={[
                { value: 'id', label: 'ID incidentu' },
                { value: 'text', label: 'Názov' },
                { value: 'category', label: 'Kategória' },
                { value: 'priority', label: 'Priorita' },
                { value: 'dueDate', label: 'Deadline' },
                { value: 'created', label: 'Dátum pridania' },
                { value: 'company', label: 'Spoločnosť' },
              ]}
            />
            <Select
              className="inc-filter-chip"
              style={{ width: 90 }}
              placeholder="A-z/Z-a"
              onChange={(value) => setSortOrder(value)}
              value={sortOrder}
              options={[
                { value: 'asc', label: 'A-z' },
                { value: 'desc', label: 'Z-a' },
              ]}
            />
          </div>
        </div>
      </div>
      <div className="inc-layout">
        <div className="inc-main">
          <IncidentsTable
            incidents={incidents}
            backlogIncidents={backlogIncidents}
            testIncidents={testIncidents}
            onRowClick={handleRowClick}
            loading={listLoading}
            groupBySystem={groupBySystem}
          />
          {!listLoading && total > 0 && (
          <Pagination
            current={filters.page}
            pageSize={filters.pageSize}
            total={total}
            onChange={handlePaginationChange}
            className="inc-pagination"
          />
          )}
          {!listLoading && (
            <div className="inc-legend">
              <div className="inc-legend-group">
                <span className="inc-legend-label">Stav</span>
                {STATUS_ORDER.map(code => (
                  <span key={code} className="inc-legend-item"><i style={{ background: LIST_STATUS_META[code].color }} />{STATUS_META[code].name}</span>
                ))}
              </div>
              <div className="inc-legend-group">
                <span className="inc-legend-label">Priorita</span>
                <span className="inc-legend-item">
                  <span className="incident-urgent-dot inc-legend-urgent-dot" />
                  Urgent
                </span>
              </div>
              <div className="inc-legend-group">
                <span className="inc-legend-label">Ikony</span>
                <span className="inc-legend-item inc-legend-icon"><PaperClipOutlined />Príloha</span>
                <span className="inc-legend-item inc-legend-icon"><MessageOutlined />Komentár</span>
              </div>
            </div>
          )}
          {!listLoading && activityTickerItems.length > 0 && (
            <div className="inc-activity-band">
              <p className="inc-activity-band-label">
                <TeamOutlined />
                Posledná aktivita
              </p>
              <div className="inc-activity-chips" aria-label="Posledná aktivita">
                {activityTickerItems.map((item) => (
                  <div
                    key={item.key}
                    className="inc-activity-chip inc-activity-chip--login"
                    title={item.title}
                  >
                    <span className="inc-activity-chip-label">{item.label}</span>
                    {item.when && (
                      <span className="inc-activity-chip-when">{item.when}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="inc-sidebars">
          <aside className="inc-stats-card">
            <div className="inc-recent-heading inc-stats-heading">
              <span>Otvorené požiadavky</span>
              <Select
                size="small"
                className="inc-stats-period"
                value={statsPeriod}
                onChange={setStatsPeriod}
                options={STATS_PERIOD_OPTS}
                popupMatchSelectWidth={false}
                style={{ minWidth: 150 }}
                disabled={statsLoading}
              />
            </div>
            {statsLoading ? (
              <SkelStats />
            ) : (
              <div className="stagger-wrap" key={`stats-${statsPeriod}-${filters.company}`}>
                <div className="stagger-in" style={{ '--i': 0 }}>
                  <OpenStatusDonut
                    received={statusTotals.received}
                    inProgress={statusTotals.inProgress}
                    onTest={statusTotals.onTest}
                    resolved={statusTotals.resolved}
                    rejected={statusTotals.rejected}
                    backlog={statusTotals.backlog}
                    onSliceClick={(statusKey) => handleFilterChange({ statuses: [statusKey] })}
                  />
                </div>
                <div className="stagger-in" style={{ '--i': 1 }}>
                  <StatusChangesBars
                    series={statusDaily}
                    periodLabel={statsPeriodLabel}
                    onDayClick={openDayDetail}
                  />
                </div>
              </div>
            )}
          </aside>

          <aside className="inc-recent-sidebar">
            <div className="inc-recent-heading">
              <CheckCircleOutlined style={{ marginRight: 6, color: '#52c41a' }} />
              Posledné vybavené požiadavky
            </div>
            <div className="inc-recent-list">
              {recentDoneLoading ? (
                <SkelRecent rows={6} />
              ) : recentDone.length === 0 ? (
                <div className="inc-recent-empty">Žiadne vybavené požiadavky</div>
              ) : (
                <div className="stagger-wrap" key={`done-${filters.company}-${recentDone[0]?.id || 0}`}>
                  {recentDone.map((inc, i) => (
                    <div
                      key={inc.id}
                      className="inc-recent-item stagger-in"
                      style={{ '--i': i }}
                      onClick={() => handleRowClick(inc.id)}
                    >
                      <div className="inc-recent-top">
                        <span className="inc-recent-niceid">{inc.niceid}</span>
                        <span className="inc-recent-date">
                          {formatRelative(inc.completionDate || inc.lastUpdate)}
                        </span>
                      </div>
                      <div className="inc-recent-text" title={inc.text}>{inc.text}</div>
                      {inc.category && <div className="inc-recent-cat">{inc.category}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>

          <aside className="inc-recent-sidebar">
            <div className="inc-recent-heading">
              <MessageOutlined style={{ marginRight: 6, color: '#5abbff' }} />
              Posledné komentáre
            </div>
            <div className="inc-recent-list">
              {recentCommentsLoading ? (
                <SkelRecent rows={6} />
              ) : recentComments.length === 0 ? (
                <div className="inc-recent-empty">Žiadne komentáre</div>
              ) : (
                <div className="stagger-wrap" key={`comments-${filters.company}-${recentComments[0]?.id || 0}`}>
                  {recentComments.map((c, i) => {
                    const plain = stripHtml(c.text);
                    return (
                      <div
                        key={c.id}
                        className="inc-recent-item stagger-in"
                        style={{ '--i': i }}
                        onClick={() => handleRowClick(c.incidentId)}
                      >
                        <div className="inc-recent-top">
                          <span className="inc-recent-niceid">{c.niceid}</span>
                          <span className="inc-recent-date">{formatRelative(c.datetime)}</span>
                        </div>
                        <div className="inc-recent-text" title={plain}>{plain}</div>
                        <div className="inc-recent-cat">{c.email}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      <ActivityDayModal
        open={dayModalOpen}
        loading={dayModalLoading}
        data={dayModalData}
        onClose={() => { setDayModalOpen(false); setDayModalData(null); }}
        onOpenIncident={(incidentId) => {
          setDayModalOpen(false);
          handleRowClick(incidentId);
        }}
      />
      <IncidentModal
        open={modalOpen}
        incident={selectedIncident}
        onClose={handleCloseModal}
        onAddComment={handleAddComment}
        fetchIncidents={() => { fetchIncidents(); fetchRecentDone(); fetchRecentComments(); }}
        emailOptions={emailOptions}
      />
    </Content>
  );
};

export default Incidents;
